import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Seo } from "../components/Seo";

export default function Lesson() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const justBooked = params.get("session_id");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // If returning from Stripe, redirect to success
  if (justBooked) { window.location.href = `/lesson/success?session_id=${justBooked}`; }

  const book = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login", { state: { from: "/lesson" }}); return; }
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/payments/lesson-checkout", {
        origin_url: window.location.origin,
        preferred_date: date, preferred_time: time, notes,
      });
      await refresh();
      window.location.href = data.url;
    } catch (e) { setErr(formatApiError(e)); setBusy(false); }
  };

  return (
    <div data-testid="lesson-page" className="min-h-screen pt-32 pb-24 px-6">
      <Seo title="In-Person Trackman Lesson — $50" description="Book a private one-hour Trackman lesson with Dr. Cuts for $50. Real ball-flight numbers, frame-by-frame video, and a repeatable cut you can take to the course." path="/lesson" />
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <div className="overline">In-person lesson</div>
          <h1 className="font-display text-5xl sm:text-6xl mt-4 leading-[0.95]">
            One hour. <span className="italic text-[#C9A227]">Trackman.</span> A real cut by the time you leave.
          </h1>
          <p className="text-white/70 mt-6 leading-relaxed">
            Book a private session at the studio with the industry-leading <strong>Trackman</strong> launch monitor —
            the same tech the pros use. We measure club path, face angle, spin axis, smash factor — every number that
            decides whether a ball cuts or doesn't.
          </p>

          <div className="mt-10 space-y-4">
            {[
              ["Trackman launch monitor", "Real ball-flight numbers, frame-by-frame video, instant playback."],
              ["60 focused minutes", "Pure coaching. No filler. We diagnose, we drill, we re-test."],
              ["Walk out with a cut", "A repeatable cue you can use on the course every day."],
            ].map((row) => (
              <div key={row[0]} className="flex gap-4 items-start border-l border-[#C9A227]/40 pl-4">
                <div>
                  <h4 className="font-display text-2xl">{row[0]}</h4>
                  <p className="text-white/60 text-sm mt-1">{row[1]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-10 border-2 border-[#C9A227] bg-[#0c1612]">
          <div className="overline">Book your slot</div>
          <div className="flex items-baseline gap-2 mt-3">
            <span className="font-display text-6xl text-[#C9A227]">$50</span>
            <span className="text-white/50 text-sm">/ one-hour session</span>
          </div>
          <p className="text-white/60 text-sm mt-3">We'll confirm your exact time by email within 24 hours.</p>

          <form onSubmit={book} className="mt-8 space-y-4">
            <div>
              <label className="overline block mb-2" style={{color:"#A0B0A8"}}>Preferred date</label>
              <input data-testid="lesson-date" required type="date" value={date}
                     onChange={(e) => setDate(e.target.value)} className="input-dark" />
            </div>
            <div>
              <label className="overline block mb-2" style={{color:"#A0B0A8"}}>Preferred time</label>
              <input data-testid="lesson-time" required type="text" value={time}
                     onChange={(e) => setTime(e.target.value)} className="input-dark"
                     placeholder="e.g. 4–5 PM, or 'weekend morning'" />
            </div>
            <div>
              <label className="overline block mb-2" style={{color:"#A0B0A8"}}>What you want to work on</label>
              <textarea data-testid="lesson-notes" rows={4} value={notes} maxLength={600}
                        onChange={(e) => setNotes(e.target.value)} className="input-dark"
                        placeholder="I keep pulling my driver dead left. Want to learn a 5-yard hold cut." />
            </div>
            {err && <div className="text-red-400 text-sm" data-testid="lesson-error">{err}</div>}
            <button data-testid="lesson-checkout-btn" disabled={busy} className="btn-primary w-full">
              {busy ? "Redirecting to Stripe..." : "Pay $50 & Reserve"}
            </button>
            <p className="text-white/40 text-xs text-center">
              Secure checkout via Stripe. Cancel up to 24h ahead for a full refund.
            </p>
            {!user && (
              <p className="text-white/60 text-xs text-center">
                <Link to="/login" className="text-[#C9A227]">Log in</Link> or <Link to="/register" className="text-[#C9A227]">register</Link> first.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
